import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Card, CardContent, Grid, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Avatar, Button, IconButton, CircularProgress, Chip,
  InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  useTheme, useMediaQuery, Snackbar, Alert, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import { exportToExcel } from '../utils/excel';

interface AuditLogItem {
  id: number;
  user: number | null;
  user_email: string;
  user_name: string;
  action: string;
  model_name: string;
  object_id: string;
  object_repr: string;
  old_values: any;
  new_values: any;
  ip_address: string;
  timestamp: string;
}

export const AuditLogsPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [activeLog, setActiveLog] = useState<AuditLogItem | null>(null);
  
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchLogs = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await apiClient.get('/core/audit-logs/');
      if (response.data && response.data.results) {
        setLogs(response.data.results);
        setFilteredLogs(response.data.results);
      }
    } catch (err: any) {
      console.error(err);
      setToast({ open: true, message: 'Fallo al cargar la bitácora de auditoría.', severity: 'error' });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchLogs();
    }
  }, [currentUser]);

  // Handle Search and Filter
  useEffect(() => {
    let result = logs;
    
    if (search) {
      const term = search.toLowerCase();
      result = result.filter(l => 
        (l.user_name && l.user_name.toLowerCase().includes(term)) ||
        (l.user_email && l.user_email.toLowerCase().includes(term)) ||
        (l.object_repr && l.object_repr.toLowerCase().includes(term)) ||
        (l.ip_address && l.ip_address.toLowerCase().includes(term)) ||
        (l.action && l.action.toLowerCase().includes(term))
      );
    }
    
    if (actionFilter !== 'all') {
      result = result.filter(l => l.action === actionFilter);
    }
    
    setFilteredLogs(result);
  }, [search, actionFilter, logs]);

  if (currentUser?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }} className="fade-in-up">
        <Card variant="outlined" sx={{ p: 4, borderRadius: 4 }}>
          <Typography variant="h5" color="error" fontWeight={700} gutterBottom>
            Acceso Denegado
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            No tienes los permisos de Administrador requeridos para auditar la bitácora de seguridad del sistema.
          </Typography>
          <Button variant="contained" color="secondary" href="/" sx={{ mt: 2 }}>
            Volver al Dashboard
          </Button>
        </Card>
      </Container>
    );
  }

  const handleOpenDetails = (logItem: AuditLogItem) => {
    setActiveLog(logItem);
    setOpenDialog(true);
  };

  const handleExportExcel = () => {
    const headers = ['ID', 'Usuario', 'Email', 'Acción', 'Módulo', 'IP', 'Detalle', 'Fecha y Hora'];
    const keys = ['id', 'user_name', 'user_email', 'action', 'model_name', 'ip_address', 'object_repr', 'timestamp'];
    exportToExcel(filteredLogs, headers, keys, 'Bitacora_Auditoria_CreaSync');
  };

  const getActionChip = (action: string) => {
    switch (action) {
      case 'login':
        return <Chip label="Sesión Iniciada" size="small" sx={{ bgcolor: 'rgba(124, 77, 255, 0.12)', color: '#7C4DFF', fontWeight: 700 }} />;
      case 'logout':
        return <Chip label="Sesión Cerrada" size="small" sx={{ bgcolor: 'rgba(108, 124, 140, 0.12)', color: '#6C7C8C', fontWeight: 700 }} />;
      case 'grade_override':
        return <Chip label="Nota Modificada" size="small" sx={{ bgcolor: 'rgba(255, 159, 67, 0.12)', color: '#FF9F43', fontWeight: 700 }} />;
      case 'grade_manual_load':
        return <Chip label="Nota Manual" size="small" sx={{ bgcolor: 'rgba(0, 176, 116, 0.12)', color: '#00B074', fontWeight: 700 }} />;
      case 'user_management':
        return <Chip label="Gestión Usuarios" size="small" sx={{ bgcolor: 'rgba(0, 112, 224, 0.12)', color: '#0070E0', fontWeight: 700 }} />;
      case 'settings_update':
        return <Chip label="Ajustes Sistema" size="small" sx={{ bgcolor: 'rgba(255, 82, 82, 0.12)', color: '#FF5252', fontWeight: 700 }} />;
      default:
        return <Chip label={action.toUpperCase()} size="small" sx={{ fontWeight: 700 }} />;
    }
  };

  const parseJSONValues = (values: any) => {
    if (!values) return null;
    if (typeof values === 'object') return values;
    try {
      return JSON.parse(values);
    } catch {
      return null;
    }
  };

  const renderSideBySideValues = (logItem: AuditLogItem) => {
    const oldVal = parseJSONValues(logItem.old_values);
    const newVal = parseJSONValues(logItem.new_values);

    if (!oldVal && !newVal) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Sin cambios de datos estructurados registrados para esta acción.
        </Typography>
      );
    }

    // Format if MoodleGrade changes
    if (logItem.model_name === 'MoodleGrade') {
      const oldGrade = oldVal?.grade !== undefined ? oldVal.grade : '-';
      const newGrade = newVal?.grade !== undefined ? newVal.grade : '-';
      const oldFeedback = oldVal?.feedback || 'Sin observaciones';
      const newFeedback = newVal?.feedback || 'Sin observaciones';

      return (
        <Box mt={2}>
          <Grid container spacing={2}>
            {/* Old Values */}
            <Grid item xs={12} sm={5}>
              <Card variant="outlined" sx={{ bgcolor: '#FFF5F5', borderColor: '#FFD8D8', p: 2 }}>
                <Typography variant="caption" fontWeight={700} color="error" display="block" sx={{ mb: 1, textTransform: 'uppercase' }}>
                  VALOR ANTERIOR (ELIMINADO / REEMPLAZADO)
                </Typography>
                <Typography variant="h5" fontWeight={900} color="#8C1D10" sx={{ mb: 0.5 }}>
                  {oldGrade !== '-' ? `${oldGrade} / 100` : '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                  Obs: {oldFeedback.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '')}
                </Typography>
              </Card>
            </Grid>

            {/* Icon */}
            <Grid item xs={12} sm={2} display="flex" alignItems="center" justifyContent="center">
              <CompareArrowsIcon sx={{ fontSize: 36, color: 'text.secondary', transform: { xs: 'rotate(90deg)', sm: 'none' } }} />
            </Grid>

            {/* New Values */}
            <Grid item xs={12} sm={5}>
              <Card variant="outlined" sx={{ bgcolor: '#E8F8F5', borderColor: '#A2E8DD', p: 2 }}>
                <Typography variant="caption" fontWeight={700} color="success.main" display="block" sx={{ mb: 1, textTransform: 'uppercase' }}>
                  VALOR NUEVO (ACTUALIZADO / ACTIVO)
                </Typography>
                <Typography variant="h5" fontWeight={900} color="#0E6251" sx={{ mb: 0.5 }}>
                  {newGrade !== '-' ? `${newGrade} / 100` : '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>
                  Obs: {newFeedback.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '')}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // Default JSON print
    return (
      <Box mt={2}>
        <Grid container spacing={2}>
          {oldVal && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" gutterBottom>
                VALORES ANTERIORES:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC', maxHeight: 150, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {JSON.stringify(oldVal, null, 2)}
                </pre>
              </Paper>
            </Grid>
          )}
          {newVal && (
            <Grid item xs={12} sm={oldVal ? 6 : 12}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" gutterBottom>
                VALORES NUEVOS:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC', maxHeight: 150, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {JSON.stringify(newVal, null, 2)}
                </pre>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const getInitials = (name: string) => {
    if (!name) return 'S';
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <Container maxWidth="lg" className="fade-in-up" sx={{ py: 4, position: 'relative' }}>
      {/* Dynamic breathing background glow */}
      <Box 
        sx={{
          position: 'absolute',
          top: '15%',
          left: '5%',
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,77,255,0.05) 0%, rgba(0,112,224,0.01) 70%)',
          filter: 'blur(45px)',
          zIndex: -1,
          animation: 'breathing-nebula 8s ease-in-out infinite'
        }}
      />
      
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={4} gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton href="/" sx={{ mr: 2, bgcolor: 'background.paper', border: '1px solid #E2E8F0' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#0A2540' }}>
              <HistoryIcon color="primary" sx={{ fontSize: 36 }} />
              Bitácora de Auditoría
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registro histórico e inmutable de seguridad, cambios académicos y sesiones del sistema
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={1.5} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            onClick={handleExportExcel}
            startIcon={<FileDownloadIcon />}
            fullWidth={isMobile}
            sx={{ 
              borderColor: '#00B074', 
              color: '#00B074', 
              borderRadius: 30,
              px: 3,
              '&:hover': {
                borderColor: '#008C5C',
                backgroundColor: 'rgba(0, 176, 116, 0.05)'
              }
            }}
          >
            Exportar Bitácora
          </Button>
          <IconButton onClick={() => fetchLogs(false)} disabled={loading} sx={{ border: '1px solid #E2E8F0', bgcolor: 'background.paper' }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters block */}
      <Card variant="outlined" sx={{ mb: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Buscar por usuario, correo, IP o detalle de acción..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3 }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="action-filter-label">Filtrar por Acción</InputLabel>
                <Select
                  labelId="action-filter-label"
                  value={actionFilter}
                  label="Filtrar por Acción"
                  onChange={(e) => setActionFilter(e.target.value)}
                  sx={{ borderRadius: 3 }}
                >
                  <MenuItem value="all">Todas las Acciones</MenuItem>
                  <MenuItem value="login">Inicios de Sesión</MenuItem>
                  <MenuItem value="logout">Cierres de Sesión</MenuItem>
                  <MenuItem value="grade_override">Modificaciones de Nota</MenuItem>
                  <MenuItem value="grade_manual_load">Cargas Manuales</MenuItem>
                  <MenuItem value="user_management">Gestión de Usuarios</MenuItem>
                  <MenuItem value="settings_update">Ajustes del Sistema</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={12}>
          <CircularProgress color="secondary" />
        </Box>
      ) : filteredLogs.length === 0 ? (
        <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron registros de auditoría
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prueba ajustando los filtros o realizando búsquedas diferentes.
          </Typography>
        </Card>
      ) : isMobile ? (
        // Mobile collapsing cards
        <Box display="flex" flexDirection="column" gap={2}>
          {filteredLogs.map(logItem => (
            <Card key={logItem.id} variant="outlined" onClick={() => handleOpenDetails(logItem)} sx={{ borderRadius: 3, p: 1.5, cursor: 'pointer' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={750} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                    {logItem.user_name || 'Sistema (Celery)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    IP: {logItem.ip_address || 'Interna'}
                  </Typography>
                </Box>
                {getActionChip(logItem.action)}
              </Box>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.5 }} noWrap>
                {logItem.object_repr}
              </Typography>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography variant="caption" color="text.secondary">
                  {new Date(logItem.timestamp).toLocaleString()}
                </Typography>
                <Button size="small" color="secondary" startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Ver Detalles
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Datatable
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'rgba(241, 245, 249, 0.6)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }} width={80}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Acción</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Detalle del Evento</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>IP de Origen</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fecha y Hora</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }} width={100}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((logItem) => (
                <TableRow key={logItem.id} hover>
                  <TableCell>#{logItem.id}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: logItem.user_name ? '#7C4DFF' : '#CBD5E1', fontSize: '0.8rem', fontWeight: 700 }}>
                        {getInitials(logItem.user_name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={750} sx={{ color: '#0A2540', lineHeight: 1.1 }}>
                          {logItem.user_name || 'Sistema'}
                        </Typography>
                        {logItem.user_email && (
                          <Typography variant="caption" color="text.secondary">
                            {logItem.user_email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{getActionChip(logItem.action)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500} sx={{ color: 'text.primary', maxWidth: 300 }} noWrap title={logItem.object_repr}>
                      {logItem.object_repr}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={logItem.ip_address || '127.0.0.1 (Local)'} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                      {new Date(logItem.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton color="secondary" onClick={() => handleOpenDetails(logItem)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={850} sx={{ color: '#0A2540', display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="secondary" />
            Detalle Completo de Auditoría #{activeLog?.id}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        {activeLog && (
          <DialogContent dividers sx={{ py: 3 }}>
            <Grid container spacing={3.5} mb={3}>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">Responsable</Typography>
                <Typography variant="body1" fontWeight={750} color="#0A2540">{activeLog.user_name || 'Sistema (Automático)'}</Typography>
                {activeLog.user_email && <Typography variant="caption" color="text.secondary">{activeLog.user_email}</Typography>}
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">Tipo de Evento</Typography>
                <Box mt={0.5}>{getActionChip(activeLog.action)}</Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography variant="caption" color="text.secondary" display="block">IP & Fecha</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>{activeLog.ip_address || 'Interna (Docker)'}</Typography>
                <Typography variant="caption" color="text.secondary">{new Date(activeLog.timestamp).toLocaleString()}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Detalle de lo Modificado</Typography>
            <Typography variant="body1" fontWeight={750} sx={{ color: '#0A2540', mb: 3 }}>
              {activeLog.object_repr}
            </Typography>

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" fontWeight={800} color="secondary" sx={{ mb: 2 }}>
              Diferenciales de Datos y Cambios Registrados:
            </Typography>
            
            {renderSideBySideValues(activeLog)}
          </DialogContent>
        )}
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenDialog(false)} variant="contained" color="primary" sx={{ borderRadius: 30, px: 4 }}>
            Cerrar Detalle
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AuditLogsPage;
